import { useState, useEffect } from "react";
import { ANIMATIONS } from "./utils/theme";
import { loadUser, saveUser, saveToken, clearUser } from "./utils/data";
import { authAPI } from "./utils/api";
import { AdminLogin, StudentLogin } from "./pages/auth/AuthPages";
import Layout from "./components/Layout";
import { AdminDashboard, AdminDegrees, AdminCourses, AdminModules,
  AdminUniversities, AdminStudents, AdminFees, AdminAssignments, AdminExams,
  AdminGrades, AdminAnnouncements } from "./pages/admin/AdminPages";
import AdminQA from "./pages/admin/QA";
import { StudentDashboard, StudentCourses, StudentLectures,
  StudentAssignments, StudentExams, StudentGrades,
  StudentAnnouncements, StudentSettings } from "./pages/student/StudentPages";
import StudentQA from "./pages/student/QA";
import Games from "./pages/student/Games";

// Nothing is reachable by guessing — only exact routes resolve to a login
// screen. Everything else (including bare "/") renders blank on purpose.
function getRoute() {
  const p = window.location.pathname;
  if (p === "/admin" || p === "/admin-login") return "admin-login";
  // /:slug/student (or /:slug/student-login, /:slug/studentlogin) — branded
  // student login (cosmetic only, same login logic underneath; the slug is
  // read directly off the URL by StudentLogin itself). A bare "/student-login"
  // with no slug no longer matches anything — there's no "default" university.
  const parts = p.split("/").filter(Boolean);
  if (parts.length === 2 && (parts[1] === "student" || parts[1] === "student-login" || parts[1] === "studentlogin")) return "student-login";
  return "blank";
}

export default function App() {
  const [route, setRoute] = useState(getRoute);
  const [user, setUser] = useState(loadUser);
  const [data, setData] = useState({
    users: [], degrees: [], programs: [], courses: [],
    modules: [], lectures: [], assignments: [], exams: [],
    submissions: [], examResults: [], announcements: [],
    qaThreads: [], notifications: [],
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState("dashboard");

  window.onpopstate = () => { if (!user) setRoute(getRoute()); };

  const handleLogin = async (u, token) => {
    saveUser(u);
    saveToken(token);
    setUser(u);
    setPage("dashboard");
    // URL is intentionally left as-is (e.g. "/admin" or "/:slug/student") so
    // signing out later returns to the same login screen, not a blank page.
  };

  const handleLogout = () => {
    clearUser();
    setUser(null);
    setRoute(getRoute());
  };

  if (!user) {
    if (route === "admin-login") return (
      <><style>{ANIMATIONS}</style>
        <AdminLogin onLogin={handleLogin} />
      </>
    );
    if (route === "student-login") return (
      <><style>{ANIMATIONS}</style>
        <StudentLogin onLogin={handleLogin} />
      </>
    );
    // "blank" — no picker page, no generic login option. Only exact routes work.
    return <style>{ANIMATIONS}</style>;
  }

  const adminPages = {
    dashboard:     <AdminDashboard user={user} />,
    degrees:       <AdminDegrees />,
    courses:       <AdminCourses />,
    modules:       <AdminModules />,
    universities:  <AdminUniversities />,
    students:      <AdminStudents />,
    fees:          <AdminFees />,
    assignments:   <AdminAssignments />,
    exams:         <AdminExams />,
    grades:        <AdminGrades />,
    announcements: <AdminAnnouncements />,
    qa_admin:      <AdminQA />,
  };

  const studentPages = {
    dashboard:       <StudentDashboard user={user} setPage={setPage} />,
    my_courses:      <StudentCourses user={user} />,
    lectures_s:      <StudentLectures user={user} />,
    assignments_s:   <StudentAssignments user={user} />,
    exams_s:         <StudentExams user={user} />,
    grades_s:        <StudentGrades user={user} />,
    announcements_s: <StudentAnnouncements />,
    qa_s:            <StudentQA user={user} />,
    games:           <Games />,
    settings_s:      <StudentSettings />,
  };

  const pages = user.role === "admin" ? adminPages : studentPages;

  return (
    <>
      <style>{ANIMATIONS}</style>
      <Layout user={user} onLogout={handleLogout} page={page} setPage={setPage}>
        {pages[page] || pages["dashboard"]}
      </Layout>
    </>
  );
}
